import { get } from './generic.api';

/**
 * Fetch AI Performance Summary
 * @param {Object} params - Query parameters
 * @param {string} params.start_date - Start date (YYYY-MM-DD)
 * @param {string} params.end_date - End date (YYYY-MM-DD)
 * @param {number} params.client_id - Client ID (optional)
 * @param {number} params.grouper_id - Grouper ID (optional)
 * @returns {Promise} Summary data
 */
export const fetchAIPerformanceSummary = async (params) => {
  try {
    const response = await get('/ai-performance/summary/', params);
    return response;
  } catch (error) {
    console.error('Failed to fetch AI performance summary:', error);
    throw error;
  }
};

/**
 * Fetch AI Performance Trends
 * @param {Object} params - Query parameters
 * @param {string} params.start_date - Start date (YYYY-MM-DD)
 * @param {string} params.end_date - End date (YYYY-MM-DD)
 * @param {number} params.client_id - Client ID (optional)
 * @param {string} params.frequency - Frequency (daily, weekly, monthly)
 * @returns {Promise} Trend data
 */
export const fetchAIPerformanceTrends = async (params) => {
  try {
    const response = await get('/ai-performance/trends/', params);
    return response;
  } catch (error) {
    console.error('Failed to fetch AI performance trends:', error);
    throw error;
  }
};

/**
 * Fetch AI Performance By Grouper
 * @param {Object} params - Query parameters
 * @param {string} params.start_date - Start date (YYYY-MM-DD)
 * @param {string} params.end_date - End date (YYYY-MM-DD)
 * @param {number} params.client_id - Client ID (optional)
 * @returns {Promise} Performance by grouper data
 */
export const fetchAIPerformanceByGrouper = async (params) => {
  try {
    const response = await get('/ai-performance/by-grouper/', params);
    return response;
  } catch (error) {
    console.error('Failed to fetch AI performance by grouper:', error);
    throw error;
  }
};

/**
 * Fetch AI Performance Distribution
 * @param {Object} params - Query parameters
 * @param {string} params.start_date - Start date (YYYY-MM-DD)
 * @param {string} params.end_date - End date (YYYY-MM-DD)
 * @param {number} params.client_id - Client ID (optional)
 * @param {number} params.grouper_id - Grouper ID (optional)
 * @returns {Promise} Distribution data
 */
export const fetchAIPerformanceDistribution = async (params) => {
  try {
    const response = await get('/ai-performance/distribution/', params);
    return response;
  } catch (error) {
    console.error('Failed to fetch AI performance distribution:', error);
    throw error;
  }
};

/**
 * Fetch AI Performance By Client
 * @param {Object} params - Query parameters
 * @param {string} params.start_date - Start date (YYYY-MM-DD)
 * @param {string} params.end_date - End date (YYYY-MM-DD)
 * @returns {Promise} Performance by client data
 */
export const fetchAIPerformanceByClient = async (params) => {
  try {
    const response = await get('/ai-performance/by-client/', params);
    return response;
  } catch (error) {
    console.error('Failed to fetch AI performance by client:', error);
    throw error;
  }
};

/**
 * Fetch AI Performance Audit Logs
 * @param {Object} params - Query parameters
 * @param {string} params.start_date - Start date (YYYY-MM-DD)
 * @param {string} params.end_date - End date (YYYY-MM-DD)
 * @param {number} params.client_id - Client ID (optional)
 * @param {number} params.grouper_id - Grouper ID (optional)
 * @param {number} params.page - Page number
 * @param {number} params.page_size - Page size
 * @returns {Promise} Audit logs data
 */
export const fetchAIPerformanceAuditLogs = async (params) => {
  try {
    const response = await get('/ai-performance/audit-logs/', params);
    return response;
  } catch (error) {
    console.error('Failed to fetch AI performance audit logs:', error);
    throw error;
  }
};

/**
 * Fetch AI Performance Comparison between two periods
 * @param {Object} params - Query parameters
 * @param {string} params.period1_start - Period 1 start date (YYYY-MM-DD)
 * @param {string} params.period1_end - Period 1 end date (YYYY-MM-DD)
 * @param {string} params.period2_start - Period 2 start date (YYYY-MM-DD)
 * @param {string} params.period2_end - Period 2 end date (YYYY-MM-DD)
 * @param {number} params.client_id - Client ID (optional)
 * @returns {Promise} Comparison data
 */
export const fetchAIPerformanceCompare = async (params) => {
  try {
    const response = await get('/ai-performance/compare/', params);
    return response;
  } catch (error) {
    console.error('Failed to fetch AI performance comparison:', error);
    throw error;
  }
};

/**
 * Fetch AI Performance Real-Time Metrics
 * @param {Object} params - Query parameters
 * @param {number} params.client_id - Client ID (optional)
 * @param {number} params.hours - Hours to look back (default: 24)
 * @returns {Promise} Real-time metrics data
 */
export const fetchAIPerformanceRealTime = async (params) => {
  try {
    const response = await get('/ai-performance/real-time/', params);
    return response;
  } catch (error) {
    console.error('Failed to fetch AI performance real-time data:', error);
    throw error;
  }
};